import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OrderConfirmationDoc, PAYMENT_METHODS, ocDefaults, ocFileName } from "@/components/OrderConfirmationDoc";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/roles";
import { api, errMsg, useApi } from "@/lib/api";

const FIELDS = [["billingName", "Billing name"], ["shippingName", "Shipping name"], ["billingAddress", "Billing address", true],
  ["shippingAddress", "Shipping address", true], ["contactNo", "Contact no."], ["gstNo", "Client GST no. (blank = N/A)"]];

function OcDetailsDialog({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => ocDefaults(project));
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    if (+f.shippingFee < 0) return toast.error("Shipping fee cannot be negative");
    try { onSaved((await api.patch(`/projects/${project.id}`, { ocDetails: { ...f, shippingFee: +f.shippingFee || 0 } })).data); toast.success("Order confirmation updated"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => { setF(ocDefaults(project)); setOpen(true); }} data-testid="edit-oc-details-button"><Pencil className="h-4 w-4 mr-2" />Edit OC details</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="oc-details-dialog">
          <DialogHeader><DialogTitle>Order confirmation details</DialogTitle>
            <DialogDescription>Filled in from the project. Changes appear only on this order confirmation.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, l, area]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label>
                {area ? <Textarea rows={2} value={f[k]} onChange={set(k)} data-testid={`oc-${k}-input`} /> : <Input value={f[k]} onChange={set(k)} data-testid={`oc-${k}-input`} />}</div>
            ))}
            <div className="space-y-1.5"><Label>Order date</Label><Input type="date" value={f.orderDate} onChange={set("orderDate")} data-testid="oc-orderDate-input" /></div>
            <div className="space-y-1.5"><Label>Shipping & handling (₹)</Label><Input type="number" min={0} value={f.shippingFee} onChange={set("shippingFee")} data-testid="oc-shippingFee-input" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Payment method</Label>
              <Select value={f.paymentMethod || undefined} onValueChange={set("paymentMethod")}>
                <SelectTrigger data-testid="oc-paymentMethod-select"><SelectValue placeholder="Choose how the customer paid" /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m} data-testid={`oc-payment-${m.replace(/\s/g, "-").toLowerCase()}`}>{m}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="oc-details-save">Save details</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: p, setData } = useApi(`/projects/${id}`);
  useEffect(() => {
    if (!p) return undefined;
    const prev = document.title;
    document.title = ocFileName(p);
    const timer = sp.get("print") === "1" ? setTimeout(() => window.print(), 600) : null;
    return () => { document.title = prev; clearTimeout(timer); };
  }, [p, sp]);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="oc-back-button"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
        {p && <span className="text-sm text-muted-foreground font-mono">{p.projectCode}</span>}
        <div className="ml-auto flex gap-2">
          {p && can(user, "projects:status") && <OcDetailsDialog project={p} onSaved={setData} />}
          <Button onClick={() => window.print()} disabled={!p} className="bg-[#d32f2f] hover:bg-[#b71c1c] text-white" data-testid="oc-print-button"><Printer className="h-4 w-4 mr-2" />Print / Download PDF</Button>
        </div>
      </div>
      {p && <div className="flex justify-center"><OrderConfirmationDoc p={p} /></div>}
    </div>
  );
}
